import { Icon } from '@factorialco/deprecated-design-system'
import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios'
import values from 'lodash/values'
import { computed, makeObservable } from 'mobx'

import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateUIToBackend, stringToDate } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'
import type { Origin } from 'modules/reports/lib/OriginStruct'
import filters from 'modules/reports/store/collections/filters'
import projections from 'modules/reports/store/collections/projections'
import type Filter from 'modules/reports/store/models/Filter'
import type { FilterParams } from 'modules/reports/store/models/Filter'
import type Projection from 'modules/reports/store/models/Projection'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'
import type {
  ReportShare,
  ReportShareMode,
  ReportShareVisibility,
} from 'modules/reports/store/models/ReportShare'

import type { IResource, ResourceData } from './Resource'

export interface Variable {
  name: string
  type: string
  value?: string
  default_value?: string
}

export interface ReportParams {
  id: string | null | undefined
  title: string
  type: string
  dashboard_id?: string
  author_id?: string
}

export interface SqlConfiguration {
  initial_data?: string | null | undefined
  secondary_data?: string | null | undefined
  tertiary_data?: string | null | undefined
}

export interface GenerateParams {
  id: string | null | undefined
  // eslint-disable-next-line factorial/no-const-collections
  projections: ProjectionParams[]
  // eslint-disable-next-line factorial/no-const-collections
  filters: FilterParams[]
  sql_query: string | null | undefined
  sql_configuration: SqlConfiguration | null | undefined
  variables: Variable[]
}

export type SaveParams = GenerateParams & {
  report: ReportParams
}

export type ChartSchema = Record<
  string,
  { params: Record<string, string[]>; requiredParams: string[] }
>

const escapeSqlString = (value: string) => {
  return value.replace(/'/g, "''")
}

export const variableValueToBackend = (type: string, value: string) => {
  if (type !== 'date') {
    return escapeSqlString(value)
  }

  if (stringToDate(value, 'backend').isValid()) {
    return value
  }

  return dateUIToBackend(value)
}

export const interpolateVariablesInQuery = (sql: string, variables: Variable[]) => {
  variables.forEach((variable: Variable) => {
    const regex = new RegExp(`{{${variable.name}}}`, 'g')
    const value = variable.value || variable.default_value || ''
    const fixedValue = variableValueToBackend(variable.type, value)
    sql = sql.replace(regex, fixedValue)
  })

  return sql
}

// eslint-disable-next-line factorial/no-const-collections
class Report extends Model<IResource> implements PolicyTarget {
  static DEFAULT_MEASUREMENT_RESULT = 'count'
  static DEFAULT_CHART_TYPES = {
    bars: 'bars',
    pie: 'pie',
    lines: 'lines',
    area: 'area',
    table: 'table',
    numeric_metric: 'numeric_metric',
  }

  static CHART_TYPES = {
    ...Report.DEFAULT_CHART_TYPES,
    scatterplot: 'scatterplot',
  }

  static ICONS: { [key: string]: React.ComponentProps<typeof Icon.Large>['icon'] } = {
    bars: 'analytics-bars',
    pie: 'analytics-pie',
    lines: 'analytics-graph-lines',
    area: 'analytics-graph',
    table: 'table-layout',
    numeric_metric: 'single-value_icon',
    scatterplot: 'nine-box',
  }

  // TODO: Date validation?
  static CHARTS_SCHEMA: ChartSchema = {
    [Report.CHART_TYPES.table]: {
      params: {},
      requiredParams: [],
    },
    [Report.CHART_TYPES.bars]: {
      params: {
        initial_data: ['number'],
        secondary_data: ['number', 'string', 'object'],
        tertiary_data: ['number', 'string', 'object'],
      },
      requiredParams: ['initial_data', 'secondary_data'],
    },
    [Report.CHART_TYPES.pie]: {
      params: {
        initial_data: ['number'],
        secondary_data: ['number', 'string', 'object'],
      },
      requiredParams: ['initial_data', 'secondary_data'],
    },
    [Report.CHART_TYPES.numeric_metric]: {
      params: {
        initial_data: ['number'],
      },
      requiredParams: ['initial_data'],
    },
    [Report.CHART_TYPES.area]: {
      params: {
        initial_data: ['number'],
        secondary_data: ['number', 'string', 'object'],
        tertiary_data: ['number', 'string', 'object'],
      },
      requiredParams: ['initial_data', 'secondary_data'],
    },
    [Report.CHART_TYPES.lines]: {
      params: {
        initial_data: ['number'],
        secondary_data: ['number', 'string', 'object'],
        tertiary_data: ['number', 'string', 'object'],
      },
      requiredParams: ['initial_data', 'secondary_data'],
    },
  }

  constructor(attr: IResource = {}) {
    super(attr)
    makeObservable(this, {
      title: computed,
      identifier: computed,
      type: computed,
      chartSelected: computed,
      projections: computed,
      filters: computed,
      dashboardId: computed,
      sqlQuery: computed,
      origin: computed,
    })
  }

  static urlRoot() {
    return '/reports/reports'
  }

  get policyTargetClass(): string {
    return '::Reports::Report'
  }

  get title(): string {
    return this.attributes.title
  }

  get identifier(): string {
    return this.has('identifier') ? this.attributes.identifier : ''
  }

  get type(): string {
    return this.attributes.chart_type
  }

  /**
   * ID of the user that the created the report.
   */
  get authorId(): string {
    return this.attributes.author_id
  }

  async generate(data: GenerateParams, cancelToken: CancelTokenSource | null) {
    const query = this.sqlQuery
    const params: AxiosRequestConfig = {
      method: 'POST',
      url: buildUrl(`${Report.staticUrl()}/generate`),
      withCredentials: true,
      data,
    }

    let response

    if (query == null) {
      if (cancelToken) {
        params['cancelToken'] = cancelToken.token
      }

      response = await axios(params)
    } else {
      const reportId = this.attributes.id
      response = await Report.executeQuery(query, data.variables, reportId)
    }

    const lastUpdatedHeader = response.data.last_update

    const parsedResponse: ResourceData = {
      lastUpdated: lastUpdatedHeader && new Date(Date.parse(response.data.last_update)),
      engine: response.data.engine,
      rows: response.data.rows,
      status: response.data.status,
    }
    return parsedResponse
  }

  static executeQuery(query: string, variables: Variable[], reportId?: string) {
    return axios({
      method: 'POST',
      url: buildUrl('/reports/query'),
      cancelToken: axios.CancelToken.source().token,
      withCredentials: true,
      data: {
        query: interpolateVariablesInQuery(query, variables),
        report_id: reportId,
        config: {
          extensions: ['stats'],
        },
      },
    })
  }

  async toSql(data: GenerateParams) {
    const { value } = await asyncAction(
      () => {
        return (this.collection || this).rpc('to_sql', data)
      },
      {
        successFlash: false,
      }
    )

    return value
  }

  async order(data: GenerateParams) {
    const { value } = await asyncAction(() => (this.collection || this).rpc('order', data), {
      successFlash: false,
    })

    return value
  }

  /**
   * Shares the report with another employee.
   * @param shareeId ID of the employee to share the report with.
   * @param mode Sharing mode @see {ReportShareModes}.
   * @param visibility Sharing visibility @see {ReportShareVisibilities}.
   */
  async share(
    shareeId: string,
    mode: ReportShareMode,
    visibility: ReportShareVisibility
  ): Promise<ShareReportResult> {
    return this.rpc(
      { rootUrl: '/api/rest/reports/report/share' },
      {
        sharee_id: shareeId,
        mode,
        visibility,
        report_id: this.attributes.id,
      }
    )
  }

  get chartSelected(): boolean {
    return values(Report.CHART_TYPES).includes(this.type)
  }

  get sqlQuery(): string | null | undefined {
    // TODO: Review if we need those this.has, because the backend is sending those attributes always
    if (this.has('sql_query')) {
      return this.attributes.sql_query
    }

    return null
  }

  get sqlConfiguration(): SqlConfiguration | null | undefined {
    // TODO: Review if we need those this.has, because the backend is sending those attributes always
    if (this.has('sql_configuration')) {
      return this.attributes.sql_configuration
    }

    return null
  }

  // eslint-disable-next-line factorial/no-const-collections
  get projections(): Projection[] {
    // TODO: Review if we need those this.has, because the backend is sending those attributes always
    if (this.has('projections')) {
      return this.attributes.projections
    }

    return projections.filter({
      report_id: this.id,
    })
  }

  // eslint-disable-next-line factorial/no-const-collections
  get filters(): Filter[] {
    // TODO: Review if we need those this.has, because the backend is sending those attributes always
    if (this.has('filters')) {
      return this.attributes.filters
    }

    return filters.filter({
      report_id: this.id,
    })
  }

  get dashboardId() {
    return this.has('dashboard_id') ? this.attributes.dashboard_id : null
  }

  get origin(): Origin | undefined {
    return this.attributes.origin
  }

  serialize(): ReportParams {
    return {
      id: this.attributes.id,
      title: this.attributes.title,
      type: this.attributes.chart_type,
      author_id: this.attributes.author_id,
    }
  }
}

/**
 * JSON returned from calling the report share endpoint
 */
interface ShareReportResult {
  // TODO: Discuss keeping this consistent with Txus (either return a full share here or make an endpoint to explicitly get report shares)
  id: ReportShare['id']
  sharee_id: string // TODO: Index on report.granteeId type
  report_id: Report['id']
  mode: ReportShareMode
  visibility: ReportShareVisibility
}

export default Report
